/**
 * Dev Admin Guard — the portal's whole auth surface.
 *
 * AXF-1013: four states, all derived from one unconditional `GET /api/usm/v1/users/me` carried by
 * the shared session cookie. There is no sign-in to redirect to any more.
 *
 *   in flight            → the centred Spin
 *   200, isDevAdmin      → the portal
 *   200, !isDevAdmin     → the refusal page
 *   401 / probe failure  → the resting page ("sign in from AX WorkFlow")
 *
 * The last row folds two probe outcomes into one screen deliberately. There is nowhere else to
 * send someone — the portal has no sign-in — and "open AX WorkFlow and sign in there" is the right
 * instruction whether the session is gone or the call failed. The difference is in the log.
 */
import { Outlet } from 'react-router-dom'
import { Spin } from 'antd'
import { useTranslation } from 'react-i18next'
import { useSnapshot } from 'valtio'
import { authStore } from '@store/authStore'
import { useCurrentUser } from '@queries/auth'
import { ForbiddenPage } from '@presentation/pages/ForbiddenPage'
import { RestingPage } from '@presentation/pages/auth/RestingPage'

export const DevAdminGuard = () => {
  const { t } = useTranslation()
  const { data: user, isPending } = useCurrentUser()
  /*
   * The probe runs once on mount, so on its own it cannot notice a session that dies WHILE the
   * tab is open — exactly the case AC-8 is about. `authStore.isAuthenticated` is the live half:
   * `client.ts`'s 401 branch calls `authActions.logout()` on any API call that comes back
   * unauthorised, and reading it here through `useSnapshot` makes that reactive again.
   *
   * This link existed before AXF-1013 and was severed when the guard moved to `useCurrentUser`
   * alone. Do not drop it: without it a staff member whose session expires mid-session sees a
   * page that still looks signed in, every click failing, with no way out but a hard refresh.
   */
  const { isAuthenticated } = useSnapshot(authStore)

  // Must exist, or every visit flashes the resting page before the probe answers (spec R8).
  if (isPending) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: '#f5f5f5',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <Spin size="large" />
        <span style={{ color: '#666' }}>{t('common.loading')}</span>
      </div>
    )
  }

  // `!user` is the probe's answer; `!isAuthenticated` is a session that died after it.
  if (!user || !isAuthenticated) return <RestingPage />
  if (!user.isDevAdmin) return <ForbiddenPage />

  return <Outlet />
}

export default DevAdminGuard
